import { copyText } from "@/lib/http";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Play } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { mh, useMhStream, upsertRun } from "@/lib/mh";
import { Panel, Eyebrow, Stat, DeptTag } from "@/components/mh/bits";
import { FlowRun } from "@/components/mh/FlowRun";
import { AgentOverview, AgentGuardrails, AgentLadder, Block } from "@/components/mh/AgentSections";
import { Playground } from "@/components/mh/Playground";
import { useAuth } from "@/context/AuthContext";
import { TailoredPanel } from "@/components/onb/TailoredPanel";
import { LearningPanel } from "@/components/onb/LearningPanel";

const TAB = "rounded-full px-4 h-8 text-xs data-[state=active]:bg-white data-[state=active]:text-black";

const Workflow = ({ a, runs, hex }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
    <Block title="Operating loop">
      <ol className="mt-3 relative border-l border-white/10 ml-2 space-y-4">
        {(a.example_workflow || []).map((s) => (
          <li key={s.step} className="pl-5 relative">
            <span className="absolute -left-[5px] top-1.5 w-2.5 h-2.5 rotate-45" style={{ background: hex }} />
            <div className="font-medium text-sm">{s.title}</div>
            <div className="text-sm text-muted-foreground">{s.detail}</div>
          </li>
        ))}
      </ol>
    </Block>
    <Block title="Recent runs">
      {runs.length === 0 && <p className="text-sm text-muted-foreground mt-3">No runs yet.</p>}
      <div className="mt-1" data-testid="agent-recent-runs">{runs.map((r) => <FlowRun key={r.id} run={r} deptHex={hex} />)}</div>
    </Block>
  </div>
);

const Prompts = ({ slug, list }) => {
  const [full, setFull] = useState(null);
  useEffect(() => { mh.prompts(slug).then((r) => setFull(r.data)).catch(() => setFull([])); }, [slug]);
  const items = full || list || [];
  return (
    <Accordion type="single" collapsible className="space-y-2" data-testid="agent-prompts">
      {items.map((p, i) => (
        <AccordionItem key={p.name} value={`p${i}`} className="border border-white/[0.07] rounded-xl bg-[#0A0A0F]/80 px-4">
          <AccordionTrigger className="text-sm hover:no-underline" data-testid={`prompt-${i}`}>
            <span>{p.name} <span className="font-mono text-[10px] text-muted-foreground ml-2">{(p.template?.length || p.length || 0).toLocaleString()} chars</span></span>
          </AccordionTrigger>
          <AccordionContent>
            {p.template ? <pre className="whitespace-pre-wrap font-mono text-xs text-muted-foreground max-h-[520px] overflow-auto" data-testid={`prompt-body-${i}`}>{p.template}</pre> : <p className="text-xs text-muted-foreground">Loading full manual…</p>}
            <button disabled={!p.template} onClick={() => { copyText(p.template); toast.success("Full prompt copied"); }} className="mt-3 font-mono text-xs underline underline-offset-4 disabled:opacity-40" data-testid={`copy-prompt-${i}`}>Copy full prompt</button>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
};

const Estimates = ({ e }) => {
  if (!e?.estimated_build_hours) return null;
  return (
    <Panel cls="p-5 grid sm:grid-cols-3 gap-5" testId="agent-estimates">
      <Stat label="Build effort" value={`${e.estimated_build_hours.min}–${e.estimated_build_hours.max}h`} sub="engineer-hours" />
      <Stat label="Run cost" value={`$${e.estimated_monthly_cost?.min}–${e.estimated_monthly_cost?.max}`} sub="per month" />
      <div><Eyebrow>Illustrative</Eyebrow><p className="text-xs text-muted-foreground mt-2">{e.assumptions}</p></div>
    </Panel>
  );
};

const ToolReadiness = ({ t }) => !t ? null : (
  <Panel cls="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3" testId="agent-tool-readiness">
    <div className="flex items-center gap-2 flex-shrink-0">
      <span className="w-2 h-2 rounded-full" style={{ background: t.ok ? "#D4FF3A" : "#FF3B5C" }} />
      <span className="text-sm font-medium" data-testid="agent-tool-status">{t.ok ? "Tools ready" : "Needs a tool connected"}</span>
    </div>
    <div className="flex flex-wrap gap-1.5 flex-1">
      {t.needs_one_of.map((n) => (
        <span key={n} className="px-2.5 h-7 inline-flex items-center rounded-full border text-xs" data-testid={t.broken?.includes(n) ? `agent-tool-broken-${n.toLowerCase().replace(/[^a-z0-9]+/g, "-")}` : undefined}
          style={{ borderColor: t.broken?.includes(n) ? "#FF3B5C88" : t.connected.includes(n) ? "#D4FF3A66" : "rgba(255,255,255,0.1)", color: t.broken?.includes(n) ? "#FF3B5C" : t.connected.includes(n) ? "#D4FF3A" : undefined }}>
          {n}{t.broken?.includes(n) ? " · broke" : ""}</span>
      ))}
    </div>
    {(!t.ok || t.broken?.length > 0) && <Link to="/connections" className="font-mono text-xs underline flex-shrink-0" data-testid="agent-connect-tools">{t.ok ? "Fix broken tool →" : "Connect one →"}</Link>}
  </Panel>
);

export default function AgentDetail() {
  const { slug } = useParams();
  const { canManage, current } = useAuth();
  const [a, setA] = useState(null);
  const [runs, setRuns] = useState([]);
  const load = useCallback(() => mh.agent(slug).then((r) => { setA(r.data); setRuns(r.data.recent_runs); }).catch(() => setA(false)), [slug]);
  useEffect(() => { load(); }, [load]);
  useMhStream((ev) => { if (ev.run?.agent_slug === slug) setRuns((l) => upsertRun(l, ev.run, 10)); });

  if (a === false) return <p className="text-muted-foreground" data-testid="agent-not-found">Agent not found.</p>;
  if (!a) return <div className="h-[50vh] grid place-items-center font-mono text-xs text-muted-foreground">Loading operator…</div>;
  const dep = a.department_info;
  const rt = a.runtime;

  const setStatus = async (s) => {
    await mh.setAgentStatus(slug, s).then((r) => {
      toast.success(`${a.name} → ${s.replace("_", " ")}`);
      if (s === "live" && !r.data.tool_check?.ok) toast.warning(`Live, but none of its tools are connected yet (${r.data.tool_check.needs_one_of.slice(0, 3).join(", ")}…)`);
      load();
    }).catch(() => toast.error("Could not update status"));
  };
  const runNow = async () => {
    await mh.runAgent(slug).then((r) => { setRuns((l) => upsertRun(l, r.data, 10)); toast.success("Run triggered"); }).catch((e) => toast.error(e.response?.data?.detail || "Run failed"));
  };

  return (
    <div className="max-w-[1400px] mx-auto space-y-6" data-testid="agent-detail-page">
      <Link to={`/departments/${dep.slug}`} className="inline-flex items-center gap-1.5 font-mono text-xs text-muted-foreground hover:text-foreground" data-testid="back-to-dept"><ArrowLeft className="w-3.5 h-3.5" /> {dep.name}</Link>
      <header className="flex flex-col lg:flex-row lg:items-end justify-between gap-5 mh-rise">
        <div className="min-w-0">
          <DeptTag name={`${dep.name} · ${a.stage}`} hex={dep.hex} />
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-2" data-testid="agent-name">{a.name}</h1>
          <p className="text-base text-muted-foreground mt-2 max-w-2xl">{a.one_liner}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Select value={rt.status} onValueChange={setStatus} disabled={!canManage}>
            <SelectTrigger className="h-10 w-40 rounded-full bg-white/[0.03] border-white/10" data-testid="agent-status-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0A0A0F] border-white/10">
              <SelectItem value="live">Live</SelectItem><SelectItem value="in_dev">In dev</SelectItem><SelectItem value="not_started">Not started</SelectItem>
            </SelectContent>
          </Select>
          <button onClick={runNow} disabled={rt.status !== "live" || !canManage} data-testid="agent-run-btn"
            className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-[#D4FF3A] text-black text-sm font-semibold hover:bg-[#e2ff73] active:scale-[0.98] transition-[background-color,transform] disabled:opacity-40">
            <Play className="w-4 h-4" /> Run now
          </button>
        </div>
      </header>

      <Panel cls="p-5 sm:p-6 grid grid-cols-2 md:grid-cols-5 gap-6">
        <Stat label="Runs today" value={rt.runs_today} testId="agent-stat-runs" accent={dep.hex} />
        <Stat label="Success" value={rt.success_rate != null ? `${rt.success_rate}%` : "—"} testId="agent-stat-success" />
        <Stat label="Latency" value={rt.avg_latency_s != null ? `${rt.avg_latency_s}s` : "—"} testId="agent-stat-latency" />
        <Stat label="Spend" value={`$${rt.cost_today}`} testId="agent-stat-cost" />
        <div><Eyebrow>Effective autonomy</Eyebrow><div className="text-sm mt-3" data-testid="agent-effective-autonomy">{a.effective_autonomy}</div><div className="font-mono text-[10px] text-muted-foreground mt-1">designed {a.autonomy} · cap {dep.autonomy_cap}</div></div>
      </Panel>

      <TailoredPanel a={a} canEdit={canManage} reload={load} clientName={current?.name} />
      <LearningPanel key={a.slug} a={a} canEdit={canManage} />
      <ToolReadiness t={a.tool_check} />

      <Tabs defaultValue="overview">
        <TabsList className="bg-white/[0.04] border border-white/10 rounded-full p-1 h-auto flex-wrap justify-start">
          <TabsTrigger value="overview" className={TAB} data-testid="agent-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="playground" className={TAB} data-testid="agent-tab-playground">Try this agent</TabsTrigger>
          <TabsTrigger value="workflow" className={TAB} data-testid="agent-tab-workflow">Workflow</TabsTrigger>
          <TabsTrigger value="ladder" className={TAB} data-testid="agent-tab-ladder">Autonomy ladder</TabsTrigger>
          <TabsTrigger value="guardrails" className={TAB} data-testid="agent-tab-guardrails">Guardrails</TabsTrigger>
          <TabsTrigger value="prompts" className={TAB} data-testid="agent-tab-prompts">Prompt manuals</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-5 space-y-4"><Estimates e={a.estimates} /><AgentOverview a={a} hex={dep.hex} /></TabsContent>
        <TabsContent value="playground" className="mt-5"><Playground agent={a} /></TabsContent>
        <TabsContent value="workflow" className="mt-5"><Workflow a={a} runs={runs} hex={dep.hex} /></TabsContent>
        <TabsContent value="ladder" className="mt-5"><AgentLadder a={a} hex={dep.hex} /></TabsContent>
        <TabsContent value="guardrails" className="mt-5"><AgentGuardrails a={a} hex={dep.hex} /></TabsContent>
        <TabsContent value="prompts" className="mt-5"><Prompts slug={slug} list={a.prompt_manuals} /></TabsContent>
      </Tabs>

      {a.related?.length > 0 && (
        <div>
          <Eyebrow cls="mb-3">Works alongside</Eyebrow>
          <div className="flex flex-wrap gap-2">
            {a.related.map((r) => <Link key={r.slug} to={`/atlas/${r.slug}`} className="px-3.5 h-9 inline-flex items-center rounded-full border border-white/10 text-sm hover:border-white/30 transition-colors" data-testid={`related-${r.slug}`}>{r.name}</Link>)}
          </div>
        </div>
      )}
    </div>
  );
}
